import dayjs from "dayjs";
import { getRightDate } from "./serviceFunctions";

// The calendar's views by layout number, as the profile stores them.
export const DAY = 0;
export const WEEK = 1;
export const MONTH = 2;
export const HALF_YEAR = 3;
export const YEAR = 4;
export const QUARTER_YEAR = 5;

// The views the bar's 1, 3, 6 and 12 buttons open, by their number of months.
export const VIEW_OF_MONTHS = { 1: MONTH, 3: QUARTER_YEAR, 6: HALF_YEAR, 12: YEAR };
const MULTI_MONTH = [QUARTER_YEAR, HALF_YEAR, YEAR];

// A home button's month, year or view that follows today, or the view the calendar is in.
export const CURRENT = 'current';

// The bar's settings, kept in the profile: how far the arrows step outside the day and month views, and the home
// buttons, 1 to 9 of them so that a home's number stays one digit. A new home is the old "Сегодня" button.
export const MAX_HOMES = 9;
export const NEW_HOME = { month: CURRENT, year: CURRENT, view: CURRENT };
export const DEFAULT_NAV = {
    step: 'week',
    homes: [NEW_HOME, { month: CURRENT, year: CURRENT, view: 6 }],
};

// The settings the profile brings, with the defaults for what it doesn't have.
export const navFromProfile = (nav) => {
    const homes = Array.isArray(nav?.homes) && nav.homes.length > 0 ? nav.homes.slice(0, MAX_HOMES) : DEFAULT_NAV.homes;
    return {
        step: nav?.step === 'month' ? 'month' : DEFAULT_NAV.step,
        homes: homes.map(home => ({ ...NEW_HOME, ...home })),
    };
};

// The first month of a 3, 6 or 12 month view: the month of its first week's Thursday.
const firstMonth = (date) => getRightDate(date).weekday(3).startOf('month');

// The quarter, half-year and year views start at the week getRightDate picks for their date, so a week's step
// goes to the Thursday of the week before or after it, which getRightDate keeps.
const shiftWeeks = (date, weeks) => getRightDate(date).weekday(3).add(weeks, 'week');

// Where the previous (-1) and next (1) buttons go: a day in the day view, a week in the week view, a month in
// the month view, and the chosen step, a week or a month, in the others.
export const stepDate = (layout, date, direction, step) => {
    switch (layout) {
        case DAY:
            return dayjs(date).add(direction, 'day');
        case WEEK:
            return dayjs(date).add(direction, 'week');
        case MONTH:
            return dayjs(date).add(direction, 'month');
        default:
            return step === 'month' ? firstMonth(date).add(direction, 'month') : shiftWeeks(date, direction);
    }
};

// The date the year view of a year starts from so that 1 January is in it: the year view starts at the week
// getRightDate picks, which for a 1 January on Friday to Sunday is the week after.
export const yearStart = (year) => {
    const january = dayjs(new Date(year, 0, 1));
    return getRightDate(january).isAfter(january) ? january.weekday(0) : january;
};

// The date a view opens on for a month: the 3, 6 and 12 month views on the month's first day, a year view of a
// January on 1 January, as the year key does.
export const monthViewDate = (layout, month) => {
    if (layout === YEAR && month.month() === 0) return yearStart(month.year());
    return MULTI_MONTH.includes(layout) ? month.startOf('month') : month;
};

// The calendar's anchor is the month it opens on, now and after a reload; null follows today. The month view
// keeps its month, the 3, 6 and 12 month views their first month. The day view keeps none: it opens on today.
export const viewMonth = (layout, date) => {
    if (layout === DAY) return undefined;
    return MULTI_MONTH.includes(layout) ? firstMonth(date) : dayjs(date).startOf('month');
};

// The profile keeps the anchor as its custom first month and year.
export const profileAnchor = (profile) => (profile.is_custom_initial_date && profile.first_month && profile.first_year
    ? dayjs(new Date(profile.first_year, profile.first_month - 1, 1))
    : null);

// The dates the views open on as the calendar starts: the anchor's month, or today and this month.
export const openingDates = (anchor, today = dayjs()) => {
    const month = anchor ?? today.startOf('month');
    return {
        dayDate: today,
        weekDate: today,
        monthDate: anchor ?? today,
        quarterYearDate: month,
        halfYearDate: month,
        yearDate: monthViewDate(YEAR, month),
    };
};

// What a home button opens: its month and year in its view. Today's month and year open today, as "Сегодня" did,
// and follow today; another month becomes the anchor.
export const homeTarget = (home, layout, today = dayjs()) => {
    const view = home.view === CURRENT ? layout : VIEW_OF_MONTHS[home.view];
    const month = home.month === CURRENT ? today.month() : home.month;
    const year = home.year === CURRENT ? today.year() : home.year;
    const follows = home.month === CURRENT && home.year === CURRENT;
    const first = dayjs(new Date(year, month, 1));
    const day = month === today.month() && year === today.year() ? today : first;
    return {
        layout: view,
        date: MULTI_MONTH.includes(view) ? monthViewDate(view, day.startOf('month')) : day,
        anchor: follows ? null : first,
    };
};

// The month the 1, 3, 6 and 12 buttons open from: the month view's month, else the anchor, else this month.
export const viewStart = (layout, date, anchor, today = dayjs()) => {
    if (layout === MONTH) return dayjs(date).startOf('month');
    return anchor ? dayjs(anchor) : today.startOf('month');
};

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const VIEW_NAMES = { 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' };
const SHORT_MONTH_NAMES = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

// A home button's tooltip, such as "Октябрь 2026, 6 месяцев".
export const homeTitle = (home) => [
    home.month === CURRENT ? 'Текущий месяц' : MONTH_NAMES[home.month],
    home.year === CURRENT ? (home.month === CURRENT ? '' : 'текущего года') : home.year,
].filter(Boolean).join(' ') + ', ' + (home.view === CURRENT ? 'текущий вид' : VIEW_NAMES[home.view]);

export { MONTH_NAMES, SHORT_MONTH_NAMES, VIEW_NAMES };
